import { useMemo } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { DxReviewHistoryEntry } from '../types';

function formatDateTime(iso: string): string {
    const date = new Date(iso);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
}

interface FeedbackStepProps {
    history: DxReviewHistoryEntry[];
    reviewerNote: string | null;
    latestRequestChangesEntry: DxReviewHistoryEntry | null;
}

/**
 * Read-only history view shown to the author after a reviewer has requested
 * changes (and to the reviewer themselves on revisits). Surfaces the most
 * recent unanswered note in an amber callout, then lists prior
 * request-changes / re-submission cycles oldest at the bottom.
 */
export function FeedbackStep({
    history,
    reviewerNote,
    latestRequestChangesEntry
}: FeedbackStepProps) {
    const requestEntries = useMemo(
        () =>
            [...history]
                .filter((e) => e.action === 'request-changes' || e.action === 'submit-for-review')
                .sort((a, b) => b.at.localeCompare(a.at)),
        [history]
    );

    return (
        <div className="flex-1 min-h-0 overflow-y-auto bg-gray-50/30">
            <div className="px-6 py-4 flex flex-col gap-4 max-w-3xl mx-auto">
                <div>
                    <div className="text-sm font-semibold text-gray-800 mb-0.5">Review history</div>
                    <div className="text-xs text-gray-500">
                        Change requests and re-submissions on this draft
                    </div>
                </div>

                {reviewerNote !== null && latestRequestChangesEntry !== null && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                        <div className="flex items-start gap-2">
                            <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-semibold text-amber-800 mb-1">
                                    Action required - change requested by{' '}
                                    {latestRequestChangesEntry.byName}
                                </div>
                                <div className="text-xs text-gray-800 whitespace-pre-wrap break-words">
                                    {reviewerNote}
                                </div>
                                <div className="text-[11px] text-gray-500 mt-2">
                                    {formatDateTime(latestRequestChangesEntry.at)}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {requestEntries.length > 0 && (
                    <div className="flex flex-col gap-2">
                        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                            History
                        </div>
                        {requestEntries.map((entry) => (
                            <div
                                key={`${entry.at}-${entry.action}`}
                                className="flex flex-col gap-1"
                            >
                                {entry.action === 'request-changes' ? (
                                    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
                                        <div className="flex items-baseline gap-2 mb-1.5">
                                            <span className="text-xs font-semibold text-gray-700">
                                                {entry.byName}
                                            </span>
                                            <span className="text-[11px] text-gray-400">
                                                requested changes
                                            </span>
                                            <span className="text-[11px] text-gray-400 ml-auto">
                                                {formatDateTime(entry.at)}
                                            </span>
                                        </div>
                                        {entry.note && (
                                            <div className="border-l-2 border-amber-300 bg-amber-50/50 px-3 py-2 text-xs text-gray-700 whitespace-pre-wrap">
                                                {entry.note}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 px-1 py-1.5">
                                        <span className="text-[11px] text-gray-500 font-medium">
                                            {entry.byName}
                                        </span>
                                        <span className="text-[11px] text-gray-400">
                                            re-submitted for review
                                        </span>
                                        <span className="text-[11px] text-gray-400 ml-auto">
                                            {formatDateTime(entry.at)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div className="h-6" />
            </div>
        </div>
    );
}
